// =============================================================================
// ui/state/useDayJournal.ts — one day's journal entry, for the Day Overview.
// -----------------------------------------------------------------------------
// A single-row read, not the whole journal. `useJournalTab` pages every entry
// because the Journal tab, the Calendar and the Coach's Report all move between
// dates; the Day Overview knows its date and needs one card's worth.
//
// ── 🚨 READ-ONLY, DELIBERATELY, AND THE TYPE IS THE GUARD ──
//
// It hands back the typed `JournalEntry` and nothing else — no `updated_at`, no
// raw blobs. So a component holding this cannot assemble a journal save even by
// accident: `savePlan` demands a `rawPlan`, `saveJournalRecap` demands a
// baseline, and neither is here. That is D62 enforced by absence rather than by
// a comment, and it matters because the typed model is LOSSY (self-assessment
// ratings and hypothesis→pattern links do not round-trip through it).
//
// ── AN ERROR IS NOT AN EMPTY DAY ──
//
// Both are surfaced separately. `entry === null` with no error means the trader
// did not journal that date; an error means the read failed and the card must
// say so rather than rendering nothing, which would be a claim about the day.
// =============================================================================

import { useEffect, useState } from 'react';
import { loadJournalEntry } from '../../io/sync/journal';
import type { JournalEntry } from '../../selectors/journal';
import { useSupabase } from './supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface DayJournalState {
  entry: JournalEntry | null;
  error: string | null;
  loading: boolean;
}

export function useDayJournal(date: string | null): DayJournalState {
  const { sb } = useSupabase();
  const [state, setState] = useState<DayJournalState>({
    entry: null, error: null, loading: !!date,
  });

  useEffect(() => {
    if (!date) { setState({ entry: null, error: null, loading: false }); return; }

    // ⚠ The same race the media hook has, and with a worse symptom here: step ‹
    // and the previous day's read can land after the new one, painting one day's
    // plan under another day's heading. Every field would look plausible.
    let alive = true;
    setState({ entry: null, error: null, loading: true });

    void (async () => {
      try {
        const entry = await loadJournalEntry(sb, date);
        if (alive) setState({ entry, error: null, loading: false });
      } catch (e) {
        if (alive) {
          setState({
            entry: null, loading: false,
            error: reportFailure('day journal', e),
          });
        }
      }
    })();

    return () => { alive = false; };
  }, [sb, date]);

  return state;
}
